import {
  Box,
  Button,
  Divider,
  FormControl,
  FormLabel,
  HStack,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Select,
  Skeleton,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { createColumnHelper, flexRender, getCoreRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';
import { SingleDatepicker } from 'components/DatePicker';
import { Pagination } from 'components/Table/Pagination';
import { useUserContext } from 'context/UserContextProvider';
import { endOfDay, format, startOfDay } from 'date-fns';
import { useLocalStorage } from 'hooks/useLocalStorage';
import usePromotionsQuery, { PromotionsParams } from 'hooks/usePromotionsQuery';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LuRotateCcw, LuSearch, LuX } from 'react-icons/lu';
import { Link as RouterLink } from 'react-router-dom';
import { useAsyncDebounce } from 'react-table';

export const PromotionsTable = () => {
  const { t, i18n } = useTranslation();
  const userContext = useUserContext();
  const countryCodes = useMemo(() => Object.values(userContext.config.countries), [userContext.config.countries]);
  const { format: formatDate } = useMemo(
    () =>
      Intl.DateTimeFormat(i18n.language, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      }),
    [i18n.language],
  );
  const storageKey = 'promotion_filters';
  const initialFilters: PromotionsParams = useMemo(
    () => ({
      page: 1,
      itemsPerPage: 5,
      country: undefined,
      startDate: null,
      endDate: null,
    }),
    [],
  );
  const [filters, setFilters] = useLocalStorage<PromotionsParams>(storageKey, initialFilters);
  const promotionsQuery = usePromotionsQuery(filters);
  const promotions = useMemo(
    () =>
      (promotionsQuery.data?.data ?? []).map((promotion) => ({
        ...promotion,
        start: new Date(promotion.start),
        end: new Date(promotion.end),
      })),
    [promotionsQuery.data],
  );
  const [search, setSearch] = useState('');
  const onDebouncedSearchChange = useAsyncDebounce((search: string) => {
    setFilters((value) => ({
      ...value,
      searchString: search,
    }));
  }, 500);
  useEffect(() => {
    setSearch(filters.searchString ?? '');
  }, [filters.searchString]);

  function deletePromotion(_promotionId: number) {}

  const columnHelper = useMemo(() => createColumnHelper<(typeof promotions)[number]>(), []);
  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: () => t('promotions:name.label'),
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor('start', {
        header: () => t('forms:active.from'),
        cell: (info) => formatDate(info.getValue()),
      }),
      columnHelper.accessor('end', {
        header: () => t('forms:active.until'),
        cell: (info) => formatDate(info.getValue()),
      }),
      columnHelper.accessor('country', {
        header: () => t('forms:countries.label'),
        cell: (info) => info.getValue().join(', '),
      }),
      columnHelper.display({
        id: 'actions',
        cell: (props) => (
          <HStack gap="8" justifyContent="end">
            <Button as={RouterLink} to={`/app/op/promotions/${props.row.original.id}/update`}>
              {t('common:update')}
            </Button>
            <Button color="red.500" onClick={() => deletePromotion(props.row.original.id)}>
              {t('common:delete')}
            </Button>
          </HStack>
        ),
      }),
    ],
    [columnHelper, formatDate, t],
  );
  const table = useReactTable({
    columns,
    data: promotions,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <Box>
      <Box mb="8">
        <HStack columnGap="8" rowGap="4" flexWrap="wrap" alignItems="end">
          <FormControl maxWidth="38ch">
            <InputGroup>
              <InputLeftElement pointerEvents="none">
                <Icon as={LuSearch} color="muted" />
              </InputLeftElement>
              <Input
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  onDebouncedSearchChange(e.target.value);
                }}
                placeholder={t('promotions:table.search')}
              />
              <InputRightElement>
                {search != '' && (
                  <IconButton
                    icon={<LuX />}
                    aria-label="Clear search"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setSearch('');
                      onDebouncedSearchChange('');
                    }}
                  />
                )}
              </InputRightElement>
            </InputGroup>
          </FormControl>
          <FormControl maxWidth="20ch">
            <FormLabel>{t('forms:start_date.label')}</FormLabel>
            <SingleDatepicker
              date={filters.startDate ? new Date(filters.startDate) : undefined}
              onDateChange={(date) =>
                setFilters((value) => ({
                  ...value,
                  startDate: date != null ? format(startOfDay(new Date(date)), 'yyyy-MM-dd HH:mm:ss') : null,
                }))
              }
              clearable
            />
          </FormControl>
          <FormControl maxWidth="20ch">
            <FormLabel>{t('forms:end_date.label')}</FormLabel>
            <SingleDatepicker
              date={filters.endDate ? new Date(filters.endDate) : undefined}
              onDateChange={(date) =>
                setFilters((value) => ({
                  ...value,
                  endDate: date != null ? format(endOfDay(new Date(date)), 'yyyy-MM-dd HH:mm:ss') : null,
                }))
              }
              clearable
            />
          </FormControl>
          <FormControl maxWidth="28ch">
            <FormLabel>{t('forms:countries.label')}</FormLabel>
            <Select
              placeholder={t('common:all')}
              value={filters.country ?? ''}
              onChange={(e) => setFilters((value) => ({ ...value, country: e.target.value || undefined }))}
            >
              {countryCodes.map((countryCode) => (
                <option key={countryCode} value={countryCode}>
                  {t(`location:countries.${countryCode}`)}
                </option>
              ))}
            </Select>
          </FormControl>
          <Button mt="4" onClick={() => setFilters(initialFilters)} leftIcon={<LuRotateCcw />}>
            {t('components:table.filters.reset')}
          </Button>
        </HStack>
      </Box>
      <TableContainer>
        <Table>
          <Thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <Tr key={headerGroup.id}>
                {promotionsQuery.isPending
                  ? Array.from({ length: columns.length }).map((_, colIndex) => (
                      <Th key={colIndex}>
                        <Skeleton height="6" variant="" />
                      </Th>
                    ))
                  : headerGroup.headers.map((header) => (
                      <Th key={header.id}>{header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}</Th>
                    ))}
              </Tr>
            ))}
          </Thead>
          <Tbody>
            {promotionsQuery.isPending
              ? Array.from({ length: 5 }).map((_, rowIndex) => (
                  <Tr key={rowIndex}>
                    {Array.from({ length: columns.length }).map((_, colIndex) => (
                      <Td key={colIndex}>
                        <Box py="2">
                          <Skeleton height="6" />
                        </Box>
                      </Td>
                    ))}
                  </Tr>
                ))
              : table.getRowModel().rows.map((row) => (
                  <Tr key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <Td key={cell.id} width={cell.column.id === 'actions' ? '20ch' : undefined}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </Td>
                    ))}
                  </Tr>
                ))}
          </Tbody>
        </Table>
        {promotionsQuery.isSuccess && table.getRowModel().rows.length === 0 && (
          <Box>
            <Box textAlign="center" py="16">
              {t('components:table.no_matches')}
            </Box>
            <Divider borderColor="gray.100" />
          </Box>
        )}
      </TableContainer>
      <Pagination
        page={filters.page}
        onPageChange={(page) => setFilters((value) => ({ ...value, page }))}
        rowsPerPage={filters.itemsPerPage}
        onRowsPerPageChange={(rowsPerPage) => setFilters((value) => ({ ...value, itemsPerPage: rowsPerPage, page: initialFilters.page }))}
        totalPageCount={Math.ceil((promotionsQuery.data?.total ?? 0) / filters.itemsPerPage)}
        firstIndex={initialFilters.page}
      />
    </Box>
  );
};
